import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { useUploadDocument } from './hooks/useDocuments';
import { useClientFolderTree, flattenClientFolders } from './hooks/useClientFolders';
import { useToast } from '@/components/ui/Toast';

interface UploadDocumentsModalProps {
  open: boolean;
  onClose: () => void;
  /** Upload target — the currently selected folder/client/job on the page. */
  folderId: string | null;
  clientId?: string;
  jobId?: string;
  /** Human-readable target label shown in the modal (e.g. folder / client name). */
  targetLabel?: string;
  /** Optional initial files (e.g. from a drag-drop onto the page). */
  initialFiles?: File[];
}

type StagedStatus = 'pending' | 'uploading' | 'done' | 'error';

interface StagedFile {
  id: string;
  file: File;
  status: StagedStatus;
  error?: string;
}

const MAX_FILE_BYTES = 25 * 1024 * 1024;

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fileGlyph(name: string): string {
  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  if (['pdf'].includes(ext)) return '📄';
  if (['xls', 'xlsx', 'csv'].includes(ext)) return '📊';
  if (['doc', 'docx'].includes(ext)) return '📝';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return '🖼️';
  if (['zip', 'rar', '7z'].includes(ext)) return '🗜️';
  return '📎';
}

let stagedSeq = 0;

/**
 * Staged multi-file upload modal. Users pick or drop files, review the list
 * (name, size, type, remove-before-upload), then upload them all to the current
 * target folder/client/job with per-file status. Replaces the old
 * "open OS picker → silent spinner" flow.
 */
export function UploadDocumentsModal({
  open,
  onClose,
  folderId,
  clientId,
  jobId,
  targetLabel,
  initialFiles,
}: UploadDocumentsModalProps) {
  const uploadDocument = useUploadDocument();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [staged, setStaged] = useState<StagedFile[]>(() =>
    (initialFiles ?? []).map((file) => ({ id: `f-${stagedSeq++}`, file, status: 'pending' })),
  );
  const [isUploading, setIsUploading] = useState(false);

  // Folder selection is mandatory when uploading for a client. Load the
  // client's folder tree and pre-select the default upload target.
  const { data: clientFolders } = useClientFolderTree(clientId);
  const { options: folderOptions, defaultId: defaultFolderId } = useMemo(
    () => flattenClientFolders(clientFolders ?? []),
    [clientFolders],
  );
  const [selectedFolderId, setSelectedFolderId] = useState<string>('');

  // Once folders load, default the selection to the page's folder (if valid),
  // else the client's default upload target, else the first folder.
  useEffect(() => {
    if (!clientId || folderOptions.length === 0) return;
    setSelectedFolderId((prev) => {
      if (prev && folderOptions.some((o) => o.value === prev)) return prev;
      if (folderId && folderOptions.some((o) => o.value === folderId)) return folderId;
      if (defaultFolderId) return defaultFolderId;
      return folderOptions[0]?.value ?? '';
    });
  }, [clientId, folderOptions, folderId, defaultFolderId]);

  // A folder must be chosen before uploading whenever the client has folders.
  const folderRequired = !!clientId && folderOptions.length > 0;
  const folderMissing = folderRequired && !selectedFolderId;

  const addFiles = useCallback((files: FileList | File[]) => {
    const incoming = Array.from(files).map((file) => {
      const tooBig = file.size > MAX_FILE_BYTES;
      return {
        id: `f-${stagedSeq++}`,
        file,
        status: (tooBig ? 'error' : 'pending') as StagedStatus,
        error: tooBig ? 'Exceeds 25MB limit' : undefined,
      };
    });
    setStaged((prev) => [...prev, ...incoming]);
  }, []);

  const removeStaged = useCallback((id: string) => {
    setStaged((prev) => prev.filter((s) => s.id !== id));
  }, []);

  const handleClose = useCallback(() => {
    if (isUploading) return;
    setStaged([]);
    onClose();
  }, [isUploading, onClose]);

  const uploadable = staged.filter((s) => s.status === 'pending' || s.status === 'error');

  const handleUpload = useCallback(async () => {
    const toUpload = staged.filter((s) => s.status === 'pending');
    if (toUpload.length === 0) return;
    setIsUploading(true);

    let successes = 0;
    for (const item of toUpload) {
      setStaged((prev) => prev.map((s) => (s.id === item.id ? { ...s, status: 'uploading' } : s)));
      try {
        await uploadDocument.mutateAsync({
          file: item.file,
          // Prefer the explicitly chosen folder; fall back to the page folder.
          folderId: selectedFolderId || folderId,
          clientId: clientId || undefined,
          jobId: jobId || undefined,
        });
        successes += 1;
        setStaged((prev) => prev.map((s) => (s.id === item.id ? { ...s, status: 'done' } : s)));
      } catch (err) {
        const message =
          (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
            ?.message || 'Upload failed';
        setStaged((prev) =>
          prev.map((s) => (s.id === item.id ? { ...s, status: 'error', error: message } : s)),
        );
      }
    }

    setIsUploading(false);

    if (successes > 0) {
      toast({
        variant: 'success',
        title: `Uploaded ${successes} file${successes !== 1 ? 's' : ''}`,
      });
    }
    // If everything succeeded, close; otherwise keep the modal so errors show.
    const remaining = staged.filter((s) => s.status === 'pending' || s.status === 'error');
    if (successes === toUpload.length && remaining.every((s) => s.status !== 'error')) {
      setStaged([]);
      onClose();
    }
  }, [staged, uploadDocument, selectedFolderId, folderId, clientId, jobId, toast, onClose]);

  const pendingCount = staged.filter((s) => s.status === 'pending').length;

  return (
    <Modal open={open} onClose={handleClose} title="Upload Documents" size="xl">
      <div className="space-y-4">
        {targetLabel && (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Uploading to{' '}
            <span className="font-medium text-gray-900 dark:text-gray-100">{targetLabel}</span>
          </p>
        )}

        {/* Mandatory folder picker (only when the client has folders) */}
        {folderRequired && (
          <Select
            label="Destination folder"
            required
            options={folderOptions}
            value={selectedFolderId}
            onChange={(e) => setSelectedFolderId(e.target.value)}
            placeholder="Select a folder…"
            error={folderMissing ? 'Choose a folder to upload into' : undefined}
            hint="Files are stored under this folder for the client."
          />
        )}

        {/* Drop zone */}
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            setDragOver(false);
          }}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
          }}
          className={`flex w-full flex-col items-center justify-center rounded-xl border-2 border-dashed px-6 py-10 text-center transition-colors ${
            dragOver
              ? 'border-brand-400 bg-brand-50 dark:border-brand-600 dark:bg-brand-900/20'
              : 'border-border hover:border-brand-300 dark:border-border-dark'
          }`}
        >
          <svg
            className="mb-2 h-8 w-8 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
            />
          </svg>
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Drop files here or click to browse
          </p>
          <p className="mt-0.5 text-xs text-gray-400">Up to 25MB per file</p>
        </button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files?.length) addFiles(e.target.files);
            e.target.value = '';
          }}
          aria-label="Choose files to upload"
        />

        {/* Staged file list */}
        {staged.length > 0 && (
          <ul className="max-h-64 space-y-2 overflow-y-auto">
            {staged.map((s) => (
              <li
                key={s.id}
                className="flex items-center gap-3 rounded-lg border border-border px-3 py-2 dark:border-border-dark"
              >
                <span className="text-lg" aria-hidden>
                  {fileGlyph(s.file.name)}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                    {s.file.name}
                  </p>
                  <p className="text-xs text-gray-400">
                    {formatBytes(s.file.size)}
                    {s.status === 'error' && s.error ? ` · ${s.error}` : ''}
                  </p>
                </div>
                <StatusPill status={s.status} />
                {(s.status === 'pending' || s.status === 'error') && !isUploading && (
                  <button
                    type="button"
                    onClick={() => removeStaged(s.id)}
                    className="rounded p-1 text-gray-400 hover:text-danger-500"
                    aria-label={`Remove ${s.file.name}`}
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between border-t border-border pt-4 dark:border-border-dark">
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {pendingCount > 0
              ? `${pendingCount} file${pendingCount !== 1 ? 's' : ''} ready`
              : 'No files ready'}
          </span>
          <div className="flex gap-3">
            <Button type="button" variant="outline" onClick={handleClose} disabled={isUploading}>
              {staged.some((s) => s.status === 'done') ? 'Close' : 'Cancel'}
            </Button>
            <Button
              type="button"
              onClick={handleUpload}
              loading={isUploading}
              disabled={pendingCount === 0 || uploadable.length === 0 || folderMissing}
            >
              {isUploading
                ? 'Uploading...'
                : `Upload ${pendingCount > 0 ? pendingCount : ''}`.trim()}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

function StatusPill({ status }: { status: StagedStatus }) {
  if (status === 'uploading') {
    return (
      <span className="flex items-center gap-1 text-xs text-brand-600 dark:text-brand-400">
        <span className="h-3 w-3 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
        Uploading
      </span>
    );
  }
  if (status === 'done') {
    return <span className="text-xs font-medium text-green-600 dark:text-green-400">Done</span>;
  }
  if (status === 'error') {
    return <span className="text-xs font-medium text-danger-600 dark:text-danger-400">Failed</span>;
  }
  return <span className="text-xs text-gray-400">Ready</span>;
}
